import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cx } from './cx';

/**
 * One period preset, or one saved address: 28px tall, full radius, and the current one in
 * `ink` on `ground` — an inversion rather than the brand, so that a row of chips has one
 * obvious member without competing with the screen's single primary button.
 */
export function Chip({
  current,
  onClick,
  onKeyDown,
  title,
  className,
  children,
}: {
  current?: boolean;
  onClick?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  title?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onKeyDown={onKeyDown}
      title={title}
      aria-pressed={current}
      className={cx(
        'inline-flex items-center h-7 px-[11px] rounded-full border text-[13px] whitespace-nowrap',
        current
          ? 'bg-ink text-ground border-ink font-semibold'
          : 'bg-transparent text-ink-2 border-rule hover:bg-surface-2',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * A row of chips that behaves like one control: arrow keys move between them, so a
 * dispatcher who reached the row with Tab does not have to Tab through six presets to leave
 * it again.
 */
export function ChipGroup({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children?: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={box}
      role="group"
      aria-label={label}
      className={cx('flex items-center gap-1.5 flex-wrap', className)}
      onKeyDown={(event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;

        if (!step || !box.current) return;

        const chips = [...box.current.querySelectorAll('button')];
        const at = chips.indexOf(document.activeElement as HTMLButtonElement);

        if (at === -1) return;

        event.preventDefault();
        chips[(at + step + chips.length) % chips.length].focus();
      }}
    >
      {children}
    </div>
  );
}
